/**
 * API Client for Bitcoin Newsletter Admin Dashboard
 *
 * Uses shared types for type-safe API communication with the FastAPI backend
 */

import { config } from '@/utils/env';
import { getAuthToken } from '@/services/auth/token-manager';

// CUR-547 god-object split: shared plumbing + per-domain method modules.
import {
  BaseApiClient,
} from './base';
import {
  applyMixins,
} from './mixins';
import {
  ArticlesApi,
} from './articles-api';
import {
  VideosApi,
} from './videos-api';
import {
  NewslettersApi,
} from './newsletters-api';
import {
  ChannelsApi,
} from './channels-api';
import {
  PatternsApi,
} from './patterns-api';
import { AuthTokensApi } from './auth-tokens-api';
import { SystemOpsApi } from './system-ops-api';
import { AdminPlatformApi } from './admin-platform-api';
import { QualityApi } from './quality-api';
import { CostAttributionApi } from './cost-attribution-api';
import { SourceGroupsApi } from './source-groups-api';
import { ChannelRollupsApi } from './channel-rollups-api';

export type { TokenGetter } from './base';

// CUR-547 batch 2: AdminAPIClient is now pure composition — every method
// lives in a per-domain module below; applyMixins merges their prototypes.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging -- intentional class/interface merge for the mixin composition pattern
export class AdminAPIClient extends BaseApiClient {}

// The interface declaration exposes the merged types; applyMixins copies the
// prototype methods. Keep both lists in sync.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging -- intentional class/interface merge for the mixin composition pattern
export interface AdminAPIClient extends ArticlesApi, VideosApi, NewslettersApi, ChannelsApi, PatternsApi, AuthTokensApi, SystemOpsApi, AdminPlatformApi, QualityApi, CostAttributionApi, SourceGroupsApi, ChannelRollupsApi {}
applyMixins(AdminAPIClient, [ArticlesApi, VideosApi, NewslettersApi, ChannelsApi, PatternsApi, AuthTokensApi, SystemOpsApi, AdminPlatformApi, QualityApi, CostAttributionApi, SourceGroupsApi, ChannelRollupsApi]);

// Response types that moved to admin-platform-api.ts in batch 2 — re-exported
// so the 40+ existing `from '@/services/api/client'` type imports keep working.
export type {
  AdminProjectsListParams,
  AdminProjectListItem,
  AdminProjectsListResponse,
  AdminProjectSourceItem,
  AdminProjectFailureItem,
  AdminProjectContentStats,
  AdminProjectDetailResponse,
  CostSummaryResponse,
  DailyCostPoint,
  CostsDailyResponse,
  ModelCost,
  CostsByModelResponse,
  ContentCost,
  CostsByContentResponse,
  McpUsageSummaryResponse,
  McpDailyPoint,
  McpUsageDailyResponse,
  McpToolUsage,
  McpUsageByToolResponse,
  AdminInboundAddressEntry,
  AdminInboundAddressListResponse,
  AdminInboundLogEntry,
  AdminInboundLogResponse,
  AdminInboundLogParams,
} from './admin-platform-api';

export type {
  QualityStep,
  QualityStepsResponse,
  QualityTrendPoint,
  QualityTrendResponse,
  QualityRecentRow,
  QualityRecentResponse,
  QualityRecentParams,
} from './quality-api';

// ============================================================================
// Default API Client Instance
// ============================================================================

// Default API client instance
export const apiClient = new AdminAPIClient(config.apiUrl, getAuthToken);
